import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Box,
  TableCell as MuiTableCell
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { Delete, Edit, Category } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableCell from '../../components/TableCell';
import ScooterTypeModal from '../../modals/ScooterTypeModal/ScooterTypeModal';
import {
  deleteScooterTypeAsync,
  getScooterTypesAsync,
  selectScooterTypes
} from '../../redux/slices/scooterTypesSlice';
import { ScooterType } from '../../resources/types/scooterTypesTypes';
import Title from '../../components/Title';
import { formatDate } from '../../resources/functions';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
  },
  transition: 'background-color 0.2s ease'
}));

const TableHeaderRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: 'transparent',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px',
  '&:first-of-type': {
    paddingLeft: '20px'
  },
  '&:last-of-type': {
    paddingRight: '20px'
  }
}));

const ScooterTypesPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getScooterTypesAsync());
  }, [dispatch]);

  const { items: scooterTypes } = useSelector(selectScooterTypes);

  const deleteScooterType = async (typeId: string) => {
    if (window.confirm('Видалити цей тип самоката?')) {
      await dispatch(deleteScooterTypeAsync(typeId));
      dispatch(getScooterTypesAsync());
    }
  };

  const [scooterTypeDialog, setScooterTypeDialog] = useState<{ open: boolean; scooterType?: ScooterType }>({
    open: false
  });

  return (
    <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Title>Типи самокатів</Title>
        <Button
          variant='contained'
          color='primary'
          onClick={() => setScooterTypeDialog({ open: true })}
        >
          Створити
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableHeaderRow>
              <TableHeaderCell>Іконка</TableHeaderCell>
              <TableHeaderCell>Назва</TableHeaderCell>
              <TableHeaderCell>Дата створення</TableHeaderCell>
              <TableHeaderCell>Дата оновлення</TableHeaderCell>
              <TableHeaderCell align='right'>Дії</TableHeaderCell>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {scooterTypes.map((type) => (
              <StyledTableRow key={type._id}>
                <TableCell>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: theme.palette.mode === 'light' 
                        ? 'rgba(80, 115, 248, 0.1)' 
                        : 'rgba(80, 115, 248, 0.2)',
                      color: '#5073F8',
                      borderRadius: '8px'
                    }}
                  >
                    <Category />
                  </Avatar>
                </TableCell>
                <TableCell>{type.name}</TableCell>
                <TableCell>{formatDate(type.createdAt)}</TableCell>
                <TableCell>{formatDate(type.updatedAt)}</TableCell>
                <TableCell align='right'>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Tooltip title='Редагувати'>
                      <IconButton
                        size='small'
                        onClick={() => setScooterTypeDialog({ open: true, scooterType: type })}
                        sx={{
                          color: theme.palette.mode === 'light' ? '#5073F8' : '#7B9CFF',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' 
                              ? 'rgba(80, 115, 248, 0.1)' 
                              : 'rgba(123, 156, 255, 0.1)'
                          }
                        }}
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Видалити'>
                      <IconButton
                        size='small'
                        onClick={() => deleteScooterType(type._id)}
                        sx={{
                          color: theme.palette.mode === 'light' ? '#F44336' : '#FF6B6B',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' 
                              ? 'rgba(244, 67, 54, 0.1)' 
                              : 'rgba(255, 107, 107, 0.1)'
                          }
                        }}
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ScooterTypeModal {...scooterTypeDialog} onClose={() => setScooterTypeDialog({ open: false })} />
    </Paper>
  );
};

export default ScooterTypesPage;
